'use client'

import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { useDebounce } from '@uidotdev/usehooks'
import { faAngleDown, faCheck } from '@document-crunch/pro-regular-svg-icons'
import {
  BasicTeam,
  useGetTeamSelectOptions,
} from '@dc-shared-web/hooks/api/team'
import { cn } from '@dc-shared-web/lib'
import { useInfiniteScroll } from '@dc-shared-web/hooks/utils'
import { Button } from '@dc-shared-web/components/shadcn/button'
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@dc-shared-web/components/shadcn/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@dc-shared-web/components/shadcn/popover'
import Spinner from '@dc-shared-web/components/base/Spinner'
import { Icon, IconColor } from '@dc-shared-web/components/base/Icon'
import { ALL_TEAMS_OPTION } from '@dc-shared-web/lib/constants'

type TeamPickerProps = {
  onChange: (team: BasicTeam) => void
  showAdminTeamsOnly?: boolean
  value?: BasicTeam
  disabled?: boolean
  showAllTeamsOption?: boolean
}

export const TeamPicker = ({
  onChange,
  showAdminTeamsOnly = false,
  value,
  disabled,
  showAllTeamsOption = false,
}: TeamPickerProps) => {
  const commandGroupRef = useRef<HTMLDivElement>(null)
  const [isCommandOpen, setIsCommandOpen] = useState(false)

  /* searchValue is debounced to prevent too many requests to the server. */
  const [searchValue, setSearchValue] = useState('')

  const { teams, isLoading, isFetchingNextPage, fetchNextPage } =
    useTeamPickerData({
      searchValue,
      showAdminTeamsOnly,
      showAllTeamsOption,
      value,
    })

  const onScroll = useInfiniteScroll(50, fetchNextPage)

  // Clear search value when component mounts or when value changes
  useEffect(() => {
    setSearchValue('')
  }, [value])

  const onItemSelect = useCallback(
    (team: BasicTeam) => {
      setIsCommandOpen(false)
      setSearchValue('')
      onChange(team)
    },
    [onChange]
  )

  const isLoadingTeams = isLoading || isFetchingNextPage

  const defaultText = showAllTeamsOption ? ALL_TEAMS_OPTION.text : ''
  /* We only use isLoading for the initial state of data but not the next page(s). */
  const triggerText =
    isLoading && !isCommandOpen ? 'Loading...' : value?.text || defaultText

  return (
    <Popover open={isCommandOpen} onOpenChange={setIsCommandOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          data-testid='team-select'
          variant='outline'
          role='combobox'
          aria-label='Team Picker trigger'
          aria-expanded={isCommandOpen}
          className='tw-w-full tw-justify-between tw-font-normal tw-text-muted-foreground tw-h-fit tw-min-h-[2.5rem]'
          disabled={disabled}
        >
          <div className='tw-line-clamp-1 tw-text-left'>{triggerText}</div>
          <Icon icon={faAngleDown} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='tw-p-0 tw-relative tw-popover-content-width-same-as-its-trigger tw-min-w-[240px] md:tw-min-w-[unset]'
        aria-label='Team picker'
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder='Search teams...'
            onInput={(e) => setSearchValue(e.currentTarget.value)}
            data-testid='team-search-input'
          />
          <CommandList className='tw-relative'>
            {!isLoadingTeams && !teams.length && (
              <div className='tw-text-center tw-text-sm tw-my-8'>
                No teams found.
              </div>
            )}
            {isFetchingNextPage && (
              <div className='tw-absolute tw-z-50 tw-w-full tw-h-full tw-bg-white/60 tw-flex tw-justify-center tw-items-center'>
                <Spinner />
              </div>
            )}
            <CommandGroup
              className='tw-max-h-64 tw-overflow-auto tw-relative'
              onScroll={onScroll}
              ref={commandGroupRef}
            >
              {isLoading && (
                <div className='tw-h-[40px] tw-flex tw-justify-center tw-items-center'>
                  <Spinner />
                </div>
              )}
              {teams.map((team) => (
                <CommandItem
                  key={team.id}
                  value={team.uuid}
                  onSelect={() => onItemSelect(team)}
                  data-testid='team-select-command-item'
                  className='tw-break-all'
                >
                  <Icon
                    icon={faCheck}
                    className={cn(
                      'tw-mr-2',
                      value?.uuid === team.uuid
                        ? 'tw-opacity-100'
                        : 'tw-opacity-0'
                    )}
                    color={IconColor.tertiary}
                  />
                  {team.text}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const useTeamPickerData = ({
  searchValue,
  showAdminTeamsOnly,
  showAllTeamsOption,
  value,
}: {
  searchValue: string
  showAdminTeamsOnly: boolean
  showAllTeamsOption: boolean
  value?: BasicTeam
}) => {
  const debouncedSearchVal = useDebounce(searchValue, 500)

  const { isLoading, isFetchingNextPage, data, fetchNextPage } =
    useGetTeamSelectOptions({ filter: debouncedSearchVal })

  const teams = useMemo(() => {
    if (isLoading) {
      return []
    }

    /* Flatten all teams from paginated data into a single array. */
    let teams =
      data?.pages.reduce((acc, page) => {
        acc.push(...page.teams)
        return acc
      }, [] as BasicTeam[]) ?? []

    if (showAdminTeamsOnly) {
      teams = teams.filter((team) => team.isAdmin)
    }

    if (showAllTeamsOption && debouncedSearchVal === '') {
      teams = [ALL_TEAMS_OPTION, ...teams]
    }

    /* Handle the value - append if not in list, or move to second position if it exists */
    if (value?.uuid && debouncedSearchVal === '') {
      const valueIndex = teams.findIndex((team) => team.uuid === value.uuid)

      if (valueIndex === -1) {
        /* Value doesn't exist in list, append it */
        if (teams.length > 0 && teams[0].uuid === ALL_TEAMS_OPTION.uuid) {
          teams = [teams[0], value, ...teams.slice(1)]
        } else {
          teams = [value, ...teams]
        }
      } else {
        /* Value exists in list, move it to first position and remove from original position */
        const valueTeam = teams[valueIndex]
        const teamsWithoutValue = teams.filter(
          (_, index) => index !== valueIndex
        )

        teams = [valueTeam, ...teamsWithoutValue]
      }
    }

    return teams
  }, [
    isLoading,
    data?.pages,
    showAdminTeamsOnly,
    showAllTeamsOption,
    debouncedSearchVal,
    value,
  ])

  return {
    teams,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
  }
}
